import type {QueryClient, QueryFilters, QueryKey} from '@tanstack/react-query'

import type {IColumnWithItems} from '../../api/columns/contracts/column-with-items'
import type {MemexColumnData} from '../../api/columns/contracts/storage'
import type {MemexItem, UpdateMemexItemResponseData} from '../../api/memex-items/contracts'
import {getEnabledFeatures} from '../../helpers/feature-flags'
import {createMemexItemModel, type MemexItemModel} from '../../models/memex-item-model'
import {mergeItemData} from './memex-item-helpers'
import type {GroupId} from './queries/query-keys'
import {
  activeQueriesMetadataPrimaryQueryKey,
  buildActiveQueriesMetadataQueryKey,
  buildMemexItemsPageQueryKey,
  groupIdForUngroupedData,
  paginatedMemexItemsPrimaryQueryKey,
} from './queries/query-keys'
import type {
  ActiveQueriesMetadata,
  MemexItemGroupsPageQueryData,
  MemexItemsPageQueryData,
  PageParam,
  PaginatedMemexItemsQueryVariables,
} from './queries/types'
import type {UseMemexItemsQueryData} from './queries/use-memex-items-query'
import {useMemexItemsQuery} from './queries/use-memex-items-query'

/**
 * As we continue to work on Memex Without Limits, we are looking to decouple
 * the move to React Query from the wider effort. This helper function allows us
 * to check whether the viewer is flagged in to the stand-alone flag for the
 * multi-page query implemenation. If not we can also check if the Memex Without
 * Limits flag is enabled.
 *
 * Either flag being enabled means that we should use the multi-page query
 * strategy.
 *
 * https://github.com/github/projects-platform/issues/802
 */
function shouldUseMultiPageQuery() {
  const {memex_table_without_limits, memex_multi_page_query} = getEnabledFeatures()
  return memex_multi_page_query || memex_table_without_limits
}

/**
 * The inverse of shouldUseMultiPageQuery, this checks various feature flags and
 * returns true if both flags are disabled.
 */
function shouldUseSinglePageQuery() {
  return !shouldUseMultiPageQuery()
}

function getKey(queryClient: QueryClient) {
  const {memex_table_without_limits} = getEnabledFeatures()
  return memex_table_without_limits ? getPaginatedQueryKeyWithVariables(queryClient) : useMemexItemsQuery.getKey()
}

function getPaginatedQueryKeyWithVariables(queryClient: QueryClient) {
  const keyWithoutVariables = [paginatedMemexItemsPrimaryQueryKey]
  const queryCache = queryClient.getQueryCache()
  const firstActiveQuery = queryCache.find(keyWithoutVariables, {exact: false, type: 'active'})
  if (!firstActiveQuery) {
    // We should not be in this state when normally interacting
    // with the query-client-api, however for tests, we don't always have an active query.
    return keyWithoutVariables
  }
  // We only want to return the first two elements of the query key, which are the
  // primary query key and the variables. We don't want to include the groupId or the pageParam
  return firstActiveQuery.queryKey.slice(0, 2)
}

function createQueryDataFromItems(items: Array<MemexItemModel>): UseMemexItemsQueryData {
  return {
    pages: [createPageQueryDataFromItems(items)],
    pageParams: [],
  }
}

function createPageQueryDataFromItems(items: Array<MemexItemModel>): MemexItemsPageQueryData {
  return {nodes: items, pageInfo: {startCursor: '', endCursor: '', hasNextPage: false, hasPreviousPage: false}}
}

export function getItemsFromQueryData(queryData?: UseMemexItemsQueryData): Array<MemexItemModel> | undefined {
  return queryData?.pages[0]?.nodes
}

function getItemsForActiveQueries(queryClient: QueryClient) {
  const baseKey = getKey(queryClient)
  const queriesData = queryClient.getQueriesData<MemexItemsPageQueryData>(baseKey)
  // The result of `getQueriesData` is an array of arrays, where the first element is the query key,
  // and the second element is the query data. We want to flatten this into a single array of query data.
  return queriesData.flatMap(q => q[1]?.nodes || [])
}

function getQueryKeyForItem(queryClient: QueryClient, id: number): QueryKey | undefined {
  const queriesData = queryClient.getQueriesData<MemexItemsPageQueryData>(activeMemexItemsQueryFilter)
  for (const [queryKey, queryData] of queriesData) {
    if (queryData?.nodes.find(item => item.id === id)) {
      return queryKey
    }
  }
  return undefined
}

// This is the query filter that we use to get the active queries for the memex items query - it can be passed
// to `setQueryData` to update the active queries for the memex items query.
// We include a `predicate` to filter out the active queries metadata query key, which is used to store the
// pagination metadata for the memex items query.
const activeMemexItemsQueryFilter: QueryFilters = {
  queryKey: [paginatedMemexItemsPrimaryQueryKey],
  exact: false,
  type: 'active',
  predicate: query => {
    return query.queryKey[query.queryKey.length - 1] !== activeQueriesMetadataPrimaryQueryKey
  },
}
/**
 * Adds a MemexItemModel to our client-side state. Does nothing if item with id already exists.
 * Will insert item at index provided, or push at the end of the array if none provided.
 * Triggers a re-render by using a new array object for the updated list of items.
 * @param queryClient
 * @param newMemexItemModel Item to add to the query client
 * @param index An optional index to insert the item at in the array. If omitted, will add to the end of the array.
 */
export function addMemexItemToQueryClient(queryClient: QueryClient, newMemexItemModel: MemexItemModel, index?: number) {
  if (shouldUseSinglePageQuery()) {
    const foundIndex = findMemexItemGlobalIndexByIdInQueryClient(queryClient, newMemexItemModel.id)

    queryClient.setQueryData<UseMemexItemsQueryData>(getKey(queryClient), queryData => {
      const oldMemexItemModels = getItemsFromQueryData(queryData)
      const newMemexItemModels = [...(oldMemexItemModels || [])]
      let insertIndex = index ?? newMemexItemModels.length

      if (foundIndex !== -1) {
        newMemexItemModels.splice(foundIndex, 1)
        insertIndex = foundIndex
      }

      newMemexItemModels.splice(insertIndex, 0, newMemexItemModel)
      return createQueryDataFromItems(newMemexItemModels)
    })
  } else {
    const foundIndex = findMemexItemGlobalIndexByIdInQueryClient(queryClient, newMemexItemModel.id)

    const itemAdded = false
    let globalIndex = 0

    const totalItems = getMemexItemModelsFromQueryClient(queryClient).length

    queryClient.setQueriesData<MemexItemsPageQueryData>(activeMemexItemsQueryFilter, queryData => {
      if (!queryData) {
        return createPageQueryDataFromItems([])
      }
      if (foundIndex !== -1) {
        // Do nothing if this item is already in the query data
        //
        // NOTE: In other cases a new object is returned, so even though we aren't changing
        // anything in the data here, return a new object to match the previous behavior.
        return {...queryData}
      }

      if (itemAdded) {
        return queryData
      }

      if (index != null && index >= globalIndex && index < globalIndex + queryData.nodes.length) {
        const newPageNodes = [...queryData.nodes]
        const indexOnPage = index - globalIndex
        newPageNodes.splice(indexOnPage, 0, newMemexItemModel)
        return {
          ...queryData,
          nodes: newPageNodes,
        }
      } else if (index == null && globalIndex + queryData.nodes.length === totalItems) {
        // If no index was provided, we want to insert the item at the end of the last page
        const newPageNodes = [...queryData.nodes]
        newPageNodes.push(newMemexItemModel)
        return {
          ...queryData,
          nodes: newPageNodes,
        }
      } else {
        globalIndex += queryData.nodes.length
        return queryData
      }
    })
  }
}

/**
 * Looks up a MemexItemModel by its id. If no item is found, undefined is returned
 * @param queryClient
 * @param id MemexItem id
 * @returns A MemexItemModel, or undefined if the item with this id does not exist
 */
export function findMemexItemByIdInQueryClient(queryClient: QueryClient, id: number) {
  if (shouldUseSinglePageQuery()) {
    const queryData = queryClient.getQueryData<UseMemexItemsQueryData>(getKey(queryClient))
    const memexItemModels = getItemsFromQueryData(queryData)
    if (memexItemModels) {
      return memexItemModels.find((item: MemexItemModel) => item.id === id)
    }
    return undefined
  } else {
    return getItemsForActiveQueries(queryClient).find((item: MemexItemModel) => item.id === id)
  }
}

/**
 * Looks up the index of a MemexItemModel by its id. If no item is found, -1 is returned.
 * If there are multiple pages of data, then the index will be the global index across all pages,
 * not within a single page of data.
 * @param queryClient
 * @param id MemexItem id
 * @returns The index of the item with provided id, or -1 if it does not exist
 */
export function findMemexItemGlobalIndexByIdInQueryClient(queryClient: QueryClient, id: number) {
  if (shouldUseSinglePageQuery()) {
    const queryData = queryClient.getQueryData<UseMemexItemsQueryData>(getKey(queryClient))
    const memexItemModels = getItemsFromQueryData(queryData)
    if (memexItemModels) {
      return memexItemModels.findIndex((item: MemexItemModel) => item.id === id)
    }
    return -1
  } else {
    return getItemsForActiveQueries(queryClient).findIndex((item: MemexItemModel) => item.id === id)
  }
}

/**
 * A thin wrapper around `getQueryData`, that returns the metadata for which query keys should be
 * active in our view
 * @param queryClient
 * @param variables PaginatedMemexItemsQueryVariables to use in our query key
 * @returns ActiveQueryData
 */
export function getActiveQueriesMetadataForVariables(
  queryClient: QueryClient,
  variables: PaginatedMemexItemsQueryVariables,
) {
  return (
    queryClient.getQueryData<ActiveQueriesMetadata>(buildActiveQueriesMetadataQueryKey(variables)) || {
      pageParams: [],
    }
  )
}

/**
 * A thin wrapper around getQueryData for getting the memex items data for a single page/query.
 * @param queryClient
 * @param variables PaginatedMemexItemsQueryVariables used in the query key
 * @param pageParam Page param for the data we're getting to be included in the query key
 * @param groupId Optional group id of the data we're getting to be included in the query key
 * @param queryData
 */
export function getQueryDataForMemexItemsPage(
  queryClient: QueryClient,
  variables: PaginatedMemexItemsQueryVariables,
  groupId: GroupId,
  pageParam: PageParam,
): MemexItemsPageQueryData | undefined {
  const queryKey = buildMemexItemsPageQueryKey(variables, groupId, pageParam)
  return queryClient.getQueryData<MemexItemsPageQueryData>(queryKey)
}

/**
 * A thin wrapper around `getQueryData`, that returns the current list of items from the QueryClient.
 * Helpful when we want to access the current state in a callback, but not trigger a re-render, for example
 * if we need to rollback in an error case after an optimistic update.
 * @param queryClient
 * @returns List of MemexItemModels representing the current state
 */
export function getMemexItemModelsFromQueryClient(queryClient: QueryClient) {
  if (shouldUseSinglePageQuery()) {
    const queryData = queryClient.getQueryData<UseMemexItemsQueryData>(getKey(queryClient))
    return getItemsFromQueryData(queryData) || []
  } else {
    return getItemsForActiveQueries(queryClient)
  }
}

/**
 * A thin wrapper around `getQueryData`, with type safety for the correct key.
 * This method is mainly exposed for use cases where we want to access the query data
 * holistically, like adding to a mutation context to support a rollback.
 * In general, if we're accessing items or page data, we should prefer a different helper.
 * @param queryClient
 * @returns query data for the `memexItems` key
 */
export function getMemexItemsQueryDataFromQueryClient(queryClient: QueryClient) {
  return queryClient.getQueryData<UseMemexItemsQueryData>(getKey(queryClient))
}

/**
 * Checks the queryClient's query data to see if we have data for any query key with the `paginatedMemexItems` primaryKey.
 * For example, if we have data for a key like `['memexItems', {q: "is:draft"}]`,
 * even if the active query key is `['memexItems', {q: "is:pr"}]`.
 * This can be used to avoid passing `initialData` in `useMemexItemsActiveQueriesMetadata`,
 * and ensure we fetch new data from the server instead.
 * @param queryClient
 * @returns Whether or not query data for any memex items related query key has been initialized
 */
export function hasMemexItemsQueryData(queryClient: QueryClient) {
  return queryClient.getQueriesData({queryKey: [paginatedMemexItemsPrimaryQueryKey]}).length !== 0
}

/**
 * Removes memex items matching ids provided from the client-side state.
 * Triggers a re-render by using a new array object for the updated list of items.
 * @param queryClient
 * @param ids List of ids matching items that should be removed.
 */
export function removeMemexItemsFromQueryClient(queryClient: QueryClient, ids: Array<number>) {
  if (shouldUseSinglePageQuery()) {
    queryClient.setQueryData<UseMemexItemsQueryData>(getKey(queryClient), queryData => {
      const oldMemexItemModels = getItemsFromQueryData(queryData) || []
      const idsSet = new Set(ids)
      const newItemModels = oldMemexItemModels.filter(item => !idsSet.has(item.id))
      return createQueryDataFromItems(newItemModels)
    })
  } else {
    const idsToRemove = new Set(ids)
    let itemsRemoved = 0

    queryClient.setQueriesData<MemexItemsPageQueryData>(activeMemexItemsQueryFilter, queryData => {
      if (!queryData) {
        return createPageQueryDataFromItems([])
      }
      // don't loop through every item on the page if we've already removed all the items we intend to
      if (itemsRemoved === idsToRemove.size) return queryData

      const newPageNodes = []
      for (const itemModel of queryData.nodes) {
        if (idsToRemove.has(itemModel.id)) {
          itemsRemoved++
        } else {
          newPageNodes.push(itemModel)
        }
      }
      return {...queryData, nodes: newPageNodes}
    })
  }
}

/**
 * A thin wrapper around `setQueryData`, that sets the metadata for which query keys should be
 * active in our view
 * @param queryClient
 * @param variables PaginatedMemexItemsQueryVariables that is used in our queryKey
 * @param newQueryData
 */
export function setActiveQueriesMetadataForVariables(
  queryClient: QueryClient,
  variables: PaginatedMemexItemsQueryVariables,
  newQueryData: ActiveQueriesMetadata,
) {
  queryClient.setQueryData<ActiveQueriesMetadata>(buildActiveQueriesMetadataQueryKey(variables), newQueryData)
}

/**
 * A thin wrapper around `setQueryData`, with type safety for the correct key.
 * This method is mainly exposed for use cases where we want to set the query data
 * holistically, if we have accessed a mutation's context and need to rollback the entire mutation.
 * In general, if we're mutating items or page data, we should prefer a different helper.
 * @param queryClient
 * @param queryData data to set in the query client for the `memexItems` key
 */
export function setMemexItemsQueryDataInQueryClient(queryClient: QueryClient, queryData: UseMemexItemsQueryData) {
  queryClient.setQueryData<UseMemexItemsQueryData>(getKey(queryClient), {...queryData})
}

/**
 * As long as structuralSharing is disabled for the query, this method will
 * force a re-render by creating an array with a new identity, but re-using our
 * existing memex item models. This is needed because our MemexItemModels are
 * mutable, so when we change a column value, nothing else will force
 * a re-render.
 *
 * If we get to a point where MemexItemModels are immutable, we should be able
 * to do away with this function.
 * @param queryClient
 */
export function setMemexItemsToForceRerenderInQueryClient(queryClient: QueryClient) {
  if (shouldUseSinglePageQuery()) {
    queryClient.setQueryData<UseMemexItemsQueryData>(getKey(queryClient), queryData => {
      const oldMemexItemModels = getItemsFromQueryData(queryData)
      return createQueryDataFromItems([...(oldMemexItemModels || [])])
    })
  } else {
    queryClient.setQueriesData<MemexItemsPageQueryData>(activeMemexItemsQueryFilter, queryData => {
      if (!queryData) {
        return createPageQueryDataFromItems([])
      }
      return {...queryData}
    })
  }
}

/**
 * A thin wrapper around setQueryData for setting the memex item group data for a single page/query.
 * @param queryClient
 * @param variables PaginatedMemexItemsQueryVariables used in the query key
 * @param pageParam Page param for the data we're setting to be included in the query key
 * @param groupId Optional group id of the data we're setting to be included in the query key
 * @param queryData
 */
export function setQueryDataForMemexItemGroupsPage(
  queryClient: QueryClient,
  variables: PaginatedMemexItemsQueryVariables,
  pageParam: PageParam,
  queryData: MemexItemGroupsPageQueryData,
) {
  const queryKey = buildMemexItemsPageQueryKey(variables, groupIdForUngroupedData, pageParam)
  queryClient.setQueryData<MemexItemGroupsPageQueryData>(queryKey, queryData)
}

/**
 * A thin wrapper around setQueryData for setting the memex items data for a single page/query.
 * @param queryClient
 * @param variables PaginatedMemexItemsQueryVariables used in the query key
 * @param pageParam Page param for the data we're setting to be included in the query key
 * @param groupId Optional group id of the data we're setting to be included in the query key
 * @param queryData
 */
export function setQueryDataForMemexItemsPage(
  queryClient: QueryClient,
  variables: PaginatedMemexItemsQueryVariables,
  groupId: GroupId,
  pageParam: PageParam,
  queryData: MemexItemsPageQueryData,
) {
  const queryKey = buildMemexItemsPageQueryKey(variables, groupId, pageParam)
  queryClient.setQueryData<MemexItemsPageQueryData>(queryKey, queryData)
}

/**
 * Updates the local state of a MemexItemModel with new values from the server. If the item cannot be found, nothing happens;
 * otherwise, a new MemexItemModel is created to replace the existing item in our client-side state and a new array identity
 * is created, so that a re-render is triggered.
 * @param queryClient
 * @param newMemexItem New data that we're looking to merge values into an existingMemexItemModel in our client state
 */
export function updateMemexItemInQueryClient(
  queryClient: QueryClient,
  newMemexItem: MemexItem | UpdateMemexItemResponseData,
) {
  if (shouldUseSinglePageQuery()) {
    const foundIndex = findMemexItemGlobalIndexByIdInQueryClient(queryClient, newMemexItem.id)

    if (foundIndex === -1) {
      return
    }

    queryClient.setQueryData<UseMemexItemsQueryData>(getKey(queryClient), queryData => {
      const oldMemexItemModels = getItemsFromQueryData(queryData)
      const newMemexItemModels = [...(oldMemexItemModels || [])]
      const oldItemModel = newMemexItemModels[foundIndex]
      if (oldItemModel) {
        const mergedItem = mergeItemData(oldItemModel, newMemexItem)
        if (mergedItem) {
          const newItemModel = createMemexItemModel(mergedItem)
          newMemexItemModels[foundIndex] = newItemModel
          return createQueryDataFromItems(newMemexItemModels)
        }
      }
      return queryData
    })
  } else {
    // The general algorithm here is as follows:
    // Find the query key of the query that contains the item we're looking to update.
    // Then call `setQueryData` with that query key, and find the index of the item in the page/query.
    // Next, we merge the item data, and update the item in the page/query, and return the new array of nodes.
    const queryKeyForQueryContainingItem = getQueryKeyForItem(queryClient, newMemexItem.id)
    if (!queryKeyForQueryContainingItem) {
      // return early if the item doesn't exist
      return
    }
    queryClient.setQueryData<MemexItemsPageQueryData>(queryKeyForQueryContainingItem, queryData => {
      if (!queryData) {
        return createPageQueryDataFromItems([])
      }
      const newPageNodes = [...queryData.nodes]
      const indexOnPage = newPageNodes.findIndex(item => item.id === newMemexItem.id)
      const itemModel = newPageNodes[indexOnPage]
      if (itemModel) {
        const mergedItem = mergeItemData(itemModel, newMemexItem)
        if (mergedItem) {
          const newItemModel = createMemexItemModel(mergedItem)
          newPageNodes[indexOnPage] = newItemModel
        }
      }
      return {...queryData, nodes: newPageNodes}
    })
  }
}

/**
 * Performs a bulk update/merge of memex item client state with a new list of items from the server.
 * Will create new MemexItemModels and merge data unless the model is marked with `skippingLiveUpdates` or
 * the item's id matches the sidePanelStateItemId parameter.
 * @param queryClient
 * @param newMemexItems full list of new memex items to merge state with existing items
 * @param sidePanelStateItemId optional parameter indicating that an item is open in the side panel
 * and we should therefore skip merging in any data to its client state
 */
export function updateMemexItemsInQueryClient(
  queryClient: QueryClient,
  newMemexItems: Array<MemexItem>,
  sidePanelStateItemId: number | undefined,
) {
  if (shouldUseSinglePageQuery()) {
    queryClient.setQueryData<UseMemexItemsQueryData>(getKey(queryClient), queryData => {
      const oldMemexItemModels = getItemsFromQueryData(queryData)
      const newMemexItemModels: Array<MemexItemModel> = []

      // Build a look-up map of MemexItemModels by id, to avoid the O(n) look-up for each item in newMemexItemModels
      // that would occur as part of findMemexItemByIdInQueryClient
      const oldMemexItemModelsMap = (oldMemexItemModels || []).reduce(
        (prev, curr) => {
          prev[curr.id] = curr
          return prev
        },
        {} as {[id: number]: MemexItemModel},
      )

      for (const item of newMemexItems) {
        let currentItemModel = oldMemexItemModelsMap[item.id]
        // This check covers three cases:
        // 1. Creating a new model for the first time
        // 2. Updating an existing model when the item exists and not skipping live updates
        // 3. Updating an existing model when the item exists and is open in the side panel
        if (!currentItemModel) {
          currentItemModel = createMemexItemModel(item)
        } else if (!currentItemModel.skippingLiveUpdates && currentItemModel.id !== sidePanelStateItemId) {
          const nextItemState = mergeItemData(currentItemModel, item)
          currentItemModel = nextItemState ? createMemexItemModel(nextItemState) : currentItemModel
        }
        newMemexItemModels.push(currentItemModel)
      }
      return createQueryDataFromItems(newMemexItemModels)
    })
  } else {
    // The general algorithm here is as follows:
    // Build up a map of new items by id, so that we can easily look up items by id
    // Call `setQueriesData` to iterate over each active query/page in the query client,
    // and then iterate over each item in that page. Update the item if appropriate, and
    // remove it from our map of items. After iterating over all items in the page, return
    // the new list of items as the query data for that query.
    const newMemexItemsById = (newMemexItems || []).reduce(
      (accumulator, memexItem) => {
        accumulator[memexItem.id] = memexItem
        return accumulator
      },
      {} as {[id: number]: MemexItem},
    )

    const setQueriesDataResult = queryClient.setQueriesData<MemexItemsPageQueryData>(
      activeMemexItemsQueryFilter,
      queryData => {
        if (!queryData) {
          return createPageQueryDataFromItems([])
        }
        const newPageNodes = [...queryData.nodes]

        for (const [indexOnPage, existingItemModel] of queryData.nodes.entries()) {
          const itemToUpdate = newMemexItemsById[existingItemModel.id]
          // if this item is skipping live updates or is open in the side panel, then we should skip updating it in
          // the cache
          if (existingItemModel.skippingLiveUpdates || existingItemModel.id === sidePanelStateItemId) {
            delete newMemexItemsById[existingItemModel.id]
          } else if (itemToUpdate) {
            const nextItemState = mergeItemData(existingItemModel, itemToUpdate)
            if (nextItemState) {
              newPageNodes.splice(indexOnPage, 1, createMemexItemModel(nextItemState))
              delete newMemexItemsById[itemToUpdate.id]
            }
          }
        }
        return {...queryData, nodes: newPageNodes}
      },
    )

    // Once we have iterated over all of the items in all of the queries, we may have some
    // items still in newMemexItemsById if they didn't previously exist. We want to add these
    // items to the last query in the list, so that they are added to the end of the list.
    // Our initial setQueriesData call returns an array of arrays, where the first element is the query key.
    // We use this to get the last query key in the list, so that we can simply call `setQueryData` with
    // an exact query key, instead of having to iterate over all of the queries again with `seQueriesData`.

    const lastQueryKey = setQueriesDataResult[setQueriesDataResult.length - 1]?.[0]

    if (lastQueryKey && Object.keys(newMemexItemsById).length > 0) {
      queryClient.setQueryData<MemexItemsPageQueryData>(lastQueryKey, queryData => {
        if (!queryData) {
          return createPageQueryDataFromItems([])
        }
        const newPageNodes = [...queryData.nodes]
        // add items that do not exist yet
        if (Object.keys(newMemexItemsById).length > 0) {
          for (const newItem of Object.values(newMemexItemsById)) {
            newPageNodes.push(createMemexItemModel(newItem))
          }
        }
        return {...queryData, nodes: newPageNodes}
      })
    }
  }
}

/**
 * Optimistically repositions an item in the list based on a new index. Also returns some information that
 * will be needed in the request to the server to reposition the item.
 * @param queryClient
 * @param id Id of item to move
 * @param index New index of item
 * @returns {previousItemId, previousMovingItemIndex}: The id of the item that the moving item should be placed after
 * (or "" if it is the first item), and the old index of the item that we're moving. If the item cannot be found, returns
 * {previousItemId: -1}
 */
export function updateMemexItemWitNewRowPositionInQueryClient(queryClient: QueryClient, id: number, index: number) {
  if (shouldUseSinglePageQuery()) {
    const itemIndex = findMemexItemGlobalIndexByIdInQueryClient(queryClient, id)

    const queryData = queryClient.getQueryData<UseMemexItemsQueryData>(getKey(queryClient))
    const oldItemModels = getItemsFromQueryData(queryData) || []

    const item = oldItemModels[itemIndex]

    if (!item) {
      return {previousItemId: -1}
    }

    // Server API expects empty string if repositioning to the first spot
    let previousItemId: number | '' = ''
    if (index > 0) {
      // If we are moving an item down (itemIndex < index), then we want to use the item at the index
      // passed to this function, because that item will be shifted up.
      // If we are moving an item up (itemIndex > index), then we want to use the item one before the index
      // passed to this function, because that item will not be moving at all
      const previousItem = itemIndex < index ? oldItemModels[index] : oldItemModels[index - 1]
      if (previousItem) previousItemId = previousItem.id
    }

    const newItemModels = [...oldItemModels]
    // Remove the item from its current position
    newItemModels.splice(itemIndex, 1)
    // Re-add the item in its new position
    newItemModels.splice(index, 0, item)

    queryClient.setQueryData<UseMemexItemsQueryData>(getKey(queryClient), createQueryDataFromItems(newItemModels))

    return {previousItemId, previousMovingItemIndex: itemIndex}
  } else {
    // Technically we could do these two things in one loop, but we're starting here with this simple approach first
    const initialIndexOfTheItemWeWantToMove = findMemexItemGlobalIndexByIdInQueryClient(queryClient, id)
    const item = findMemexItemByIdInQueryClient(queryClient, id)
    if (!item) return {previousItemId: -1}

    // The API expects empty string if repositioning to the first spot
    let previousItemId: number | '' = ''

    let currentGlobalIndex = 0
    queryClient.setQueriesData<MemexItemsPageQueryData>(activeMemexItemsQueryFilter, queryData => {
      if (!queryData) {
        return createPageQueryDataFromItems([])
      }
      const globalIndexBeforeThisPage = currentGlobalIndex
      currentGlobalIndex = currentGlobalIndex + queryData.nodes.length

      const oldItemNeedsToBeRemovedOnThisPage =
        initialIndexOfTheItemWeWantToMove >= globalIndexBeforeThisPage &&
        initialIndexOfTheItemWeWantToMove < currentGlobalIndex
      const newItemNeedsToBeAddedOnThisPage = index >= globalIndexBeforeThisPage && index < currentGlobalIndex

      // no work to be done on this page
      if (!oldItemNeedsToBeRemovedOnThisPage && !newItemNeedsToBeAddedOnThisPage) return queryData

      // if index is 0 we should leave previousItemId as '' to indicate it's the first position
      if (newItemNeedsToBeAddedOnThisPage && index > 0) {
        // If we are moving an item down (itemIndex < index), then we want to find the item at the same index that
        // was passed to this function because that item will be shifted up.
        //
        // If we are moving an item up (itemIndex > index), then we want to find the item one before the index
        // passed to this function because that item will not be moving at all.
        const indexToFindPreviousItem =
          initialIndexOfTheItemWeWantToMove < index
            ? index - globalIndexBeforeThisPage
            : index - globalIndexBeforeThisPage - 1

        const previousItem = queryData.nodes[indexToFindPreviousItem]
        if (previousItem) previousItemId = previousItem.id
      }

      const newPageNodes = [...queryData.nodes]

      if (newItemNeedsToBeAddedOnThisPage) {
        const indexToAddItemAt =
          initialIndexOfTheItemWeWantToMove < index
            ? index - globalIndexBeforeThisPage - 1
            : index - globalIndexBeforeThisPage
        newPageNodes.splice(indexToAddItemAt, 0, item)
      }

      if (oldItemNeedsToBeRemovedOnThisPage) {
        newPageNodes.splice(newPageNodes.indexOf(item), 1)
      }

      return {...queryData, nodes: newPageNodes}
    })

    return {previousItemId, previousMovingItemIndex: initialIndexOfTheItemWeWantToMove}
  }
}

/**
 * Updates the column data values for a given column for all items.
 * Sets an item's column value to 'null' if no column data is provided
 * for that item.
 * @param queryClient
 * @param columnWithItems The column and new item values to update to
 */
export function updateMemexItemsForColumnInQueryClient(queryClient: QueryClient, columnWithItems: IColumnWithItems) {
  if (shouldUseSinglePageQuery()) {
    // Build up a map for our new column values to avoid O(n^2) look-up in the
    // second for loop
    const newColumnValuesByItemId: {[itemId: number]: MemexColumnData} = {}
    if (columnWithItems.memexProjectColumnValues) {
      for (const columnData of columnWithItems.memexProjectColumnValues) {
        newColumnValuesByItemId[columnData.memexProjectItemId] = {
          memexProjectColumnId: columnWithItems.id,
          value: columnData.value,
        } as MemexColumnData
      }
    }
    queryClient.setQueryData<UseMemexItemsQueryData>(getKey(queryClient), queryData => {
      const oldMemexItemModels = getItemsFromQueryData(queryData)
      const newMemexItemModels = [...(oldMemexItemModels || [])]
      for (const itemModel of newMemexItemModels) {
        let newValue: MemexColumnData | undefined = newColumnValuesByItemId[itemModel.id]
        // If we don't have a value, we need to create one with a value of null
        if (!newValue?.value) {
          newValue = {memexProjectColumnId: columnWithItems.id, value: null} as MemexColumnData
        }
        itemModel.setColumnValueForItemColumnType(newValue)
      }
      return createQueryDataFromItems(newMemexItemModels)
    })
  } else {
    // Build up a map for our new column values to avoid O(n^2) look-up in the
    // second for loop
    const newColumnValuesByItemId: {[itemId: number]: MemexColumnData} = {}
    if (columnWithItems.memexProjectColumnValues) {
      for (const columnData of columnWithItems.memexProjectColumnValues) {
        newColumnValuesByItemId[columnData.memexProjectItemId] = {
          memexProjectColumnId: columnWithItems.id,
          value: columnData.value,
        } as MemexColumnData
      }
    }
    queryClient.setQueriesData<MemexItemsPageQueryData>(activeMemexItemsQueryFilter, queryData => {
      if (!queryData) {
        return createPageQueryDataFromItems([])
      }
      const newPageNodes = [...queryData.nodes]
      for (const itemModel of newPageNodes) {
        let newValue: MemexColumnData | undefined = newColumnValuesByItemId[itemModel.id]
        // If we don't have a value, we need to create one with a value of null
        if (!newValue?.value) {
          newValue = {memexProjectColumnId: columnWithItems.id, value: null} as MemexColumnData
        }
        itemModel.setColumnValueForItemColumnType(newValue)
      }
      return {...queryData, nodes: newPageNodes}
    })
  }
}
